/**
 * E2E Test Fixtures for CustomComparisonTab
 * Used ONLY in E2E mode (REACT_APP_E2E_MODE=true)
 *
 * Security: This file is bundled into the app but the test route
 * that uses it is conditionally registered only when E2E mode is enabled.
 */
import { getConfig } from "../config/runtimeConfig";

export interface E2EResourceColumn {
  column_name: string;
  is_active: boolean;
}

export interface E2EResource {
  rId: number;
  resource_name: string;
  rCode: string;
  resource_column_properties: {
    resource_columns: E2EResourceColumn[];
  };
}

export interface E2ERule {
  id: number;
  name: string;
  filter_rules: any[];
  query_sql?: string;
  tolerance?: {
    tolerance_type: string;
    tolerance_value: number | null;
    fallback_value: number | null;
  };
}

/**
 * Mock resources with realistic column sets
 * Structured to match the backend API format with resource_column_properties
 */
export const E2E_RESOURCES: any[] = [
  {
    rId: 1,
    resource_name: "FactSet_Position",
    rCode: "FACT",
    resource_column_properties: {
      resource_columns: [
        { column_name: "portfolio_code", is_active: true },
        { column_name: "security_id", is_active: true },
        { column_name: "quantity", is_active: true },
        { column_name: "market_value", is_active: true },
        { column_name: "unit_cost", is_active: true },
        { column_name: "total_cost", is_active: true },
        { column_name: "accrued_interest", is_active: true },
        { column_name: "unrealized_gain_loss", is_active: true },
        { column_name: "position_date", is_active: true },
        { column_name: "account_number", is_active: true },
        { column_name: "customer_id", is_active: true },
        { column_name: "customer_name", is_active: true },
        { column_name: "customer_email", is_active: true },
        { column_name: "trade_date", is_active: true },
        { column_name: "settlement_date", is_active: true },
        { column_name: "currency_code", is_active: true },
        { column_name: "exchange_rate", is_active: true },
      ],
    },
  },
  {
    rId: 2,
    resource_name: "GWP_Position",
    rCode: "GWP",
    resource_column_properties: {
      resource_columns: [
        { column_name: "portfolio_code", is_active: true },
        { column_name: "security_id", is_active: true },
        { column_name: "quantity", is_active: true },
        { column_name: "market_value", is_active: true },
        { column_name: "unit_cost", is_active: true },
        { column_name: "total_cost", is_active: true },
        { column_name: "accrued_interest", is_active: true },
        { column_name: "unrealized_gain_loss", is_active: true },
        { column_name: "position_date", is_active: true },
        { column_name: "account_number", is_active: true },
        { column_name: "customer_id", is_active: true },
        { column_name: "customer_name", is_active: true },
        { column_name: "customer-email", is_active: true }, // Deliberately different format
        { column_name: "trade_date", is_active: true },
        { column_name: "settlement_date", is_active: true },
        { column_name: "currency_code", is_active: true },
        { column_name: "exchange_rate", is_active: true },
      ],
    },
  },
];

/**
 * Generates 105+ test rules with various configurations
 */
export function generateE2ERules(count: number = 105): E2ERule[] {
  const baseRuleNames = [
    "portfolio_code",
    "security_id",
    "quantity",
    "market_value",
    "unit_cost",
    "total_cost",
    "accrued_interest",
    "unrealized_gain_loss",
    "position_date",
    "account_number",
    "customer_id",
    "Customer Name", // Exact match with space
    "customer-email", // Normalized match test
    "trade_date",
    "settlement_date",
    "nonexistent_field", // No match test
    "currency_code",
    "exchange_rate",
  ];

  const rules: E2ERule[] = [];
  let idCounter = 1;

  for (let i = 0; i < count; i++) {
    const baseName = baseRuleNames[i % baseRuleNames.length];
    const ruleName =
      i >= baseRuleNames.length
        ? `${baseName}_${Math.floor(i / baseRuleNames.length)}`
        : baseName;

    rules.push({
      id: idCounter++,
      name: ruleName,
      filter_rules: [],
    });
  }

  return rules;
}

/**
 * Resource columns data for context
 * This represents columns already selected/used in rules - keep empty for E2E testing
 * so all columns are available for selection
 */
export const E2E_SELECTED_RESOURCE_COLUMNS = E2E_RESOURCES.map((resource) => ({
  rId: resource.rId,
  code: resource.rCode,
  columns: [], // Empty = no columns are marked as "already used"
}));

/**
 * Default tolerance data
 */
export const E2E_TOLERANCE_DATA = {
  tolerance_type: "percentage",
  tolerance_value: 0.01,
  fallback_value: null,
};

/**
 * Environment flag check - returns true only in E2E mode
 */
export function isE2EMode(): boolean {
  return getConfig("REACT_APP_E2E_MODE") === "true";
}
